import React, { useState } from "react";
import {
  Eye,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  User,
  RotateCcw,
  X,
  Check,
  Ban,
  History,
} from "lucide-react";
import { Payment } from "../../../utils/database";
import OperatorStatusResult from "../details/OperatorStatusResult";

interface TransactionItemProps {
  transaction: Payment;
  onAction: (action: string, transaction: Payment, data?: any) => any;
}

type VerificationSide = { key: string; label: string; country_code?: string };

const TransactionItem: React.FC<TransactionItemProps> = ({ transaction, onAction }) => {
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showChangeStatusModal, setShowChangeStatusModal] = useState(false);
  const [statusSide, setStatusSide] = useState<string | null>(null);
  const [verificationSides, setVerificationSides] = useState<VerificationSide[]>([]);
  const [loadingSides, setLoadingSides] = useState(false);
  const [refundOperator, setRefundOperator] = useState("");
  const [refundPhone, setRefundPhone] = useState(transaction.phone || "");
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [loadingChangeStatus, setLoadingChangeStatus] = useState(false);
  const [loadingCancel, setLoadingCancel] = useState(false);
  const [statusData, setStatusData] = useState<any>(null);
  const [selectedPayoutStatus, setSelectedPayoutStatus] = useState("SUCCESS");
  const [selectedTransactionStatus, setSelectedTransactionStatus] = useState("SUCCEEDED");

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      SUCCEEDED: {
        bg: "bg-emerald-100",
        text: "text-emerald-700",
        border: "border-emerald-300",
        label: "Réussi",
        icon: CheckCircle,
      },
      FAILED: {
        bg: "bg-red-100",
        text: "text-red-700",
        border: "border-red-300",
        label: "Échoué",
        icon: XCircle,
      },
      REQUIRES_ACTION: {
        bg: "bg-amber-100",
        text: "text-amber-700",
        border: "border-amber-300",
        label: "Action Requise",
        icon: AlertTriangle,
      },
      PROCESSING: {
        bg: "bg-blue-100",
        text: "text-blue-700",
        border: "border-blue-300",
        label: "En Cours",
        icon: Clock,
      },
      PENDING: {
        bg: "bg-blue-100",
        text: "text-blue-700",
        border: "border-blue-300",
        label: "En Attente",
        icon: Clock,
      },
      echeck: {
        bg: "bg-indigo-100",
        text: "text-indigo-700",
        border: "border-indigo-300",
        label: "eCheck",
        icon: Clock,
      },
      CANCELED: {
        bg: "bg-gray-100",
        text: "text-gray-700",
        border: "border-gray-300",
        label: "Annulé",
        icon: Ban,
      },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.PENDING;
    const Icon = config.icon;

    return (
      <div
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${config.bg} ${config.text} border ${config.border}`}
      >
        <Icon className="w-3.5 h-3.5" />
        {config.label}
      </div>
    );
  };

  const getPayoutBadge = (payoutStatus: string | null) => {
    if (!payoutStatus) {
      return (
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-300">
          <Clock className="w-3.5 h-3.5" />
          En Attente
        </div>
      );
    }

    const upper = payoutStatus.toUpperCase();
    const isSuccess = upper === "SUCCESS" || upper === "COMPLETED" || upper === "SUCCEEDED";
    const isPending =
      upper === "PENDING" ||
      upper === "PROCESSING" ||
      upper === "INITIATED" ||
      upper === "PENDING_MANUAL" ||
      upper === "PENDING_MANUAL_PAYOUT";
    const isCanceled = upper === "CANCELED" || upper === "CANCELLED";

    if (isSuccess) {
      return (
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border bg-emerald-100 text-emerald-700 border-emerald-300">
          <CheckCircle className="w-3.5 h-3.5" />
          Distribué
        </div>
      );
    }
    if (isPending) {
      return (
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border bg-blue-100 text-blue-700 border-blue-300">
          <Clock className="w-3.5 h-3.5" />
          En Attente
        </div>
      );
    }
    if (isCanceled) {
      return (
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border bg-gray-100 text-gray-700 border-gray-300">
          <Ban className="w-3.5 h-3.5" />
          Annulé
        </div>
      );
    }

    return (
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border bg-red-100 text-red-700 border-red-300">
        <XCircle className="w-3.5 h-3.5" />
        Échec
      </div>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatAmount = (amount: number, currency: string) => {
    return `${amount.toLocaleString()} ${currency}`;
  };

  const handleRefundSubmit = () => {
    if (!refundOperator) {
      alert("Veuillez sélectionner un opérateur");
      return;
    }

    onAction("refund", transaction, {
      operator: refundOperator,
      phone: refundPhone,
      amount: transaction.total_cad,
    });

    setShowRefundModal(false);
    setRefundOperator("");
    setRefundPhone(transaction.phone || "");
  };

  const handleCheckStatus = async (side: string) => {
    setStatusSide(side);
    setLoadingStatus(true);
    try {
      const response = await onAction("check-status", transaction, { side });
      setStatusData(response);
    } catch (error) {
      setStatusData({ error: "Erreur lors de la récupération du statut" });
    } finally {
      setLoadingStatus(false);
    }
  };

  const openStatusModal = async () => {
    setShowStatusModal(true);
    setStatusSide(null);
    setStatusData(null);
    setLoadingSides(true);
    try {
      const sides = await onAction("load-verification-sides", transaction);
      setVerificationSides(
        Array.isArray(sides) && sides.length > 0
          ? sides
          : [
              { key: "canada", label: "Canada" },
              { key: "cameroun", label: "Cameroun" },
            ]
      );
    } catch {
      setVerificationSides([
        { key: "canada", label: "Canada" },
        { key: "cameroun", label: "Cameroun" },
      ]);
    } finally {
      setLoadingSides(false);
    }
  };

  const closeStatusModal = () => {
    setShowStatusModal(false);
    setStatusData(null);
    setStatusSide(null);
    setVerificationSides([]);
  };

  const statusSideLabel: string =
    verificationSides.find((s) => s.key === statusSide)?.label ||
    (statusSide === "cameroun" ? "Cameroun" : statusSide === "canada" ? "Canada" : (statusSide ?? "Afrique"));

  const isEcheckStatus = transaction.status === "FAILED";
  const canChangeStatus = transaction.payout_status !== "SUCCESS" && transaction.payout_status !== "COMPLETED";
  const isCanceled = transaction.status === "CANCELED";

  const handleCancelTransaction = async () => {
    if (!window.confirm("Êtes-vous sûr de vouloir annuler cette transaction ?")) return;
    setLoadingCancel(true);
    try {
      await onAction("change-status", transaction, {
        payout_status: "CANCELED",
        transaction_status: "CANCELED",
      });
    } catch (error) {
      console.error("Erreur lors de l'annulation:", error);
    } finally {
      setLoadingCancel(false);
    }
  };

  const handleChangeStatus = async () => {
    setLoadingChangeStatus(true);
    try {
      await onAction("change-status", transaction, {
        payout_status: selectedPayoutStatus,
        transaction_status: selectedTransactionStatus,
      });
      setShowChangeStatusModal(false);
    } catch (error) {
      console.error("Erreur lors du changement de statut:", error);
    } finally {
      setLoadingChangeStatus(false);
    }
  };

  const handleViewUserHistory = () => {
    if (transaction.user) {
      onAction("view-user-history", transaction, { user: transaction.user });
    }
  };

  return (
    <>
      <div className="mb-3 bg-white rounded-lg border border-gray-200 shadow-sm transition-shadow hover:shadow-md">
        {/* Row Principal */}
        <div className="p-4">
          <div className="grid grid-cols-12 gap-4 items-center">
            {/* ID & Date */}
            <div className="col-span-2">
              <p className="font-mono font-semibold text-gray-900">#{transaction.id?.slice(-8) || "N/A"}</p>
              <p className="mt-1 text-xs text-gray-500">{formatDate(transaction.created_at)}</p>
            </div>

            {/* Client */}
            <div className="col-span-2">
              {transaction.user ? (
                <div className="flex gap-2 items-center">
                  <div className="flex flex-shrink-0 justify-center items-center w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full">
                    <span className="text-sm font-bold text-white">
                      {transaction.user.first_name?.charAt(0)}
                      {transaction.user.last_name?.charAt(0)}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {transaction.user.first_name} {transaction.user.last_name}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{transaction.user.email}</p>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2 items-center">
                  <div className="flex flex-shrink-0 justify-center items-center w-10 h-10 bg-gray-200 rounded-full">
                    <User className="w-5 h-5 text-gray-500" />
                  </div>
                  <span className="font-mono text-xs text-gray-600">
                    {transaction.user_id ? `${transaction.user_id.slice(0, 8)}...` : "Guest"}
                  </span>
                </div>
              )}
            </div>

            {/* Montants */}
            <div className="col-span-2">
              <p className="text-lg font-bold text-gray-900">{formatAmount(transaction.total_cad, "CAD")}</p>
              <p className="mt-1 text-xs text-gray-500">→ {formatAmount(transaction.amount_fcfa, "FCFA")}</p>
              {(transaction.account_before != null || transaction.account_after != null) && (
                <div className="mt-2 pt-2 border-t border-gray-100 space-y-0.5">
                  <p className="text-xs font-medium text-gray-500">Solde wallet</p>
                  {transaction.account_before != null && (
                    <p className="text-xs text-gray-600">
                      Avant:{" "}
                      <span className="font-semibold">{Number(transaction.account_before).toLocaleString()}</span>
                    </p>
                  )}
                  {transaction.account_after != null && (
                    <p className="text-xs text-gray-600">
                      Après: <span className="font-semibold">{Number(transaction.account_after).toLocaleString()}</span>
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Destinataire — toujours visible (payout_name / payout_phonenumber) */}
            <div className="col-span-2 min-w-0">
              <p className="text-xs font-medium text-gray-500 mb-0.5">Destinataire</p>
              <p className="text-sm font-semibold text-gray-900 truncate" title={transaction.payout_name || undefined}>
                {transaction.payout_name?.trim() || "—"}
              </p>
              <p className="mt-1 text-xs text-gray-600 truncate">
                {transaction.payout_phonenumber?.trim() || transaction.phone || "—"}
              </p>
              <p className="mt-1 text-xs text-gray-500 truncate">{transaction.operator || "—"}</p>
            </div>

            {/* Provider */}
            <div className="col-span-1">
              <span className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs font-bold bg-gradient-to-r from-blue-100 to-blue-50 text-blue-700 border border-blue-200">
                {transaction.provider}
              </span>
            </div>

            {/* Taux de conversion */}
            <div className="col-span-1">
              <p className="text-sm font-semibold text-gray-900">
                {transaction.conversion_rate ? transaction.conversion_rate.toFixed(2) : "N/A"}
              </p>
              <p className="mt-1 text-xs text-gray-500">Taux</p>
            </div>

            {/* Statuts */}
            <div className="flex flex-col col-span-1 gap-2">
              {getStatusBadge(transaction.status)}
              {getPayoutBadge(transaction.payout_status)}
            </div>

            {/* Actions */}
            <div className="flex flex-col col-span-1 gap-1">
              <button
                onClick={() => onAction("view", transaction)}
                className="p-1.5 text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded transition-colors border border-blue-200 flex items-center justify-center"
                title="Voir détails"
              >
                <Eye className="w-3.5 h-3.5" />
              </button>

              <button
                onClick={handleViewUserHistory}
                className="p-1.5 text-xs font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded transition-colors border border-indigo-200 flex items-center justify-center"
                title="Historique de l'utilisateur"
              >
                <History className="w-3.5 h-3.5" />
              </button>

              <button
                onClick={openStatusModal}
                className="p-1.5 text-xs font-semibold text-purple-700 bg-purple-50 hover:bg-purple-100 rounded transition-colors border border-purple-200 flex items-center justify-center"
                title="Vérifier"
              >
                <RefreshCw className="w-3.5 h-3.5" />
              </button>
              {transaction.status === "FAILED" && (
                <button
                  onClick={() => onAction("retry", transaction)}
                  className="p-1.5 text-xs font-semibold text-orange-700 bg-orange-50 hover:bg-orange-100 rounded transition-colors border border-orange-200 flex items-center justify-center"
                  title="Réessayer"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                </button>
              )}
              {isEcheckStatus && (
                <button
                  onClick={() => {
                    setShowRefundModal(true);
                    setRefundOperator("");
                    setRefundPhone(transaction.phone || "");
                  }}
                  className="p-1.5 text-xs font-semibold text-red-700 bg-red-50 hover:bg-red-100 rounded transition-colors border border-red-200 flex items-center justify-center"
                  title="Rembourser"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                </button>
              )}
              {canChangeStatus && (
                <button
                  onClick={() => setShowChangeStatusModal(true)}
                  className="p-1.5 text-xs font-semibold text-green-700 bg-green-50 hover:bg-green-100 rounded transition-colors border border-green-200 flex items-center justify-center"
                  title="Marquer comme réussi"
                >
                  <Check className="w-3.5 h-3.5" />
                </button>
              )}
              {!isCanceled && (
                <button
                  onClick={handleCancelTransaction}
                  disabled={loadingCancel}
                  className="p-1.5 text-xs font-semibold text-gray-700 bg-gray-50 hover:bg-gray-200 rounded transition-colors border border-gray-300 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Annuler la transaction"
                >
                  {loadingCancel ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Ban className="w-3.5 h-3.5" />}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal Remboursement */}
      {showRefundModal && (
        <div className="flex overflow-y-auto fixed inset-0 z-50 justify-center items-center p-4 bg-black bg-opacity-50">
          <div className="my-8 w-full max-w-md bg-white rounded-2xl shadow-2xl">
            <div className="flex flex-shrink-0 justify-between items-center px-6 py-5 bg-gradient-to-r from-red-600 to-red-700 rounded-t-2xl">
              <h2 className="text-xl font-bold text-white">Remboursement</h2>
              <button
                onClick={() => setShowRefundModal(false)}
                className="flex-shrink-0 p-1 text-white rounded-lg transition-colors hover:bg-red-800"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="overflow-y-auto max-h-[calc(100vh-200px)]">
              <div className="p-6 space-y-6">
                <div>
                  <label className="block mb-3 text-sm font-bold text-gray-900">Sélectionner l'opérateur</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setRefundOperator("ORANGE")}
                      className={`p-4 border-2 rounded-xl font-semibold transition-all ${
                        refundOperator === "ORANGE"
                          ? "border-orange-500 bg-orange-50 text-orange-700"
                          : "border-gray-300 text-gray-700 hover:border-orange-300 bg-gray-50"
                      }`}
                    >
                      Orange
                    </button>
                    <button
                      onClick={() => setRefundOperator("MTN")}
                      className={`p-4 border-2 rounded-xl font-semibold transition-all ${
                        refundOperator === "MTN"
                          ? "border-yellow-500 bg-yellow-50 text-yellow-700"
                          : "border-gray-300 text-gray-700 hover:border-yellow-300 bg-gray-50"
                      }`}
                    >
                      MTN
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block mb-2 text-sm font-bold text-gray-900">Numéro de téléphone</label>
                  <input
                    type="tel"
                    value={refundPhone}
                    onChange={(e) => setRefundPhone(e.target.value)}
                    className="px-4 py-3 w-full font-semibold rounded-xl border-2 border-gray-300 outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="+237 6XX XXX XXX"
                  />
                </div>

                <div className="p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border-2 border-gray-200">
                  <p className="mb-2 text-xs font-semibold text-gray-600">Montant du remboursement</p>
                  <p className="text-3xl font-black text-gray-900">{formatAmount(transaction.total_cad, "CAD")}</p>
                  <p className="mt-2 text-xs text-gray-500">Automatiquement récupéré</p>
                </div>

                <div className="p-4 bg-red-50 rounded-xl border-2 border-red-200">
                  <p className="text-sm text-red-800">
                    <span className="font-bold">Attention:</span> Un remboursement sera effectué vers{" "}
                    <span className="font-bold">{refundPhone || "le numéro fourni"}</span>
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-shrink-0 gap-3 px-6 py-4 bg-gray-50 rounded-b-2xl border-t-2 border-gray-200">
              <button
                onClick={() => setShowRefundModal(false)}
                className="flex-1 px-4 py-3 font-bold text-gray-700 rounded-lg border-2 border-gray-300 transition-colors hover:bg-gray-100"
              >
                Annuler
              </button>
              <button
                onClick={handleRefundSubmit}
                disabled={!refundOperator}
                className="flex-1 px-4 py-3 font-bold text-white bg-red-600 rounded-lg transition-colors hover:bg-red-700 disabled:bg-gray-400"
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Changement de Statut */}
      {showChangeStatusModal && (
        <div className="flex overflow-y-auto fixed inset-0 z-50 justify-center items-center p-4 bg-black bg-opacity-50">
          <div className="my-8 w-full max-w-md bg-white rounded-2xl shadow-2xl">
            <div className="flex flex-shrink-0 justify-between items-center px-6 py-5 bg-gradient-to-r from-green-600 to-green-700 rounded-t-2xl">
              <h2 className="text-xl font-bold text-white">Confirmer le changement de statut</h2>
              <button
                onClick={() => setShowChangeStatusModal(false)}
                className="flex-shrink-0 p-1 text-white rounded-lg transition-colors hover:bg-green-800"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="overflow-y-auto max-h-[calc(100vh-200px)]">
              <div className="p-6 space-y-6">
                <div className="p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border-2 border-gray-200">
                  <p className="mb-2 text-xs font-semibold text-gray-600">Transaction ID</p>
                  <p className="font-mono text-lg font-black text-gray-900">#{transaction.id?.slice(-8) || "N/A"}</p>
                </div>

                <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border-2 border-blue-200">
                  <p className="mb-2 text-xs font-semibold text-blue-600">Montant</p>
                  <p className="text-2xl font-black text-blue-900">{formatAmount(transaction.total_cad, "CAD")}</p>
                  <p className="mt-1 text-sm text-blue-700">→ {formatAmount(transaction.amount_fcfa, "FCFA")}</p>
                </div>

                <div className="p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border-2 border-gray-200">
                  <p className="mb-2 text-xs font-semibold text-gray-600">Bénéficiaire</p>
                  <p className="text-lg font-bold text-gray-900">{transaction.operator}</p>
                  <p className="font-mono text-sm text-gray-700">{transaction.phone}</p>
                </div>

                <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl border-2 border-purple-200">
                  <p className="mb-3 text-xs font-semibold text-purple-600">Statut du Payout</p>
                  <select
                    value={selectedPayoutStatus}
                    onChange={(e) => setSelectedPayoutStatus(e.target.value)}
                    className="px-4 py-3 w-full text-sm font-semibold text-gray-900 bg-white rounded-lg border-2 border-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="SUCCESS">SUCCESS</option>
                    <option value="PENDING">PENDING</option>
                    <option value="FAILED">FAILED</option>
                    <option value="CANCELED">CANCELED</option>
                    <option value="REFUNDED">REFUNDED</option>
                    <option value="PROCESSING">PROCESSING</option>
                    <option value="COMPLETED">COMPLETED</option>
                  </select>
                </div>

                <div className="p-4 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl border-2 border-indigo-200">
                  <p className="mb-3 text-xs font-semibold text-indigo-600">Statut de la Transaction</p>
                  <select
                    value={selectedTransactionStatus}
                    onChange={(e) => setSelectedTransactionStatus(e.target.value)}
                    className="px-4 py-3 w-full text-sm font-semibold text-gray-900 bg-white rounded-lg border-2 border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="SUCCEEDED">SUCCEEDED</option>
                    <option value="FAILED">FAILED</option>
                    <option value="PENDING">PENDING</option>
                    <option value="PROCESSING">PROCESSING</option>
                    <option value="CANCELED">CANCELED</option>
                    <option value="REFUNDED">REFUNDED</option>
                    <option value="CREATED">CREATED</option>
                    <option value="REQUIRES_ACTION">REQUIRES_ACTION</option>
                  </select>
                </div>

                <div className="p-4 bg-yellow-50 rounded-xl border-2 border-yellow-200">
                  <p className="text-sm text-yellow-800">
                    <span className="font-bold">Attention:</span> Cette action va modifier le statut du payout en{" "}
                    <span className="font-bold">{selectedPayoutStatus}</span> et le statut de la transaction en{" "}
                    <span className="font-bold">{selectedTransactionStatus}</span>.
                  </p>
                </div>

                <div className="p-4 bg-green-50 rounded-xl border-2 border-green-200">
                  <p className="text-sm text-green-800">
                    <span className="font-bold">Confirmation:</span> Êtes-vous sûr de vouloir appliquer ces changements
                    ?
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-shrink-0 gap-3 px-6 py-4 bg-gray-50 rounded-b-2xl border-t-2 border-gray-200">
              <button
                onClick={() => setShowChangeStatusModal(false)}
                disabled={loadingChangeStatus}
                className="flex-1 px-4 py-3 font-bold text-gray-700 rounded-lg border-2 border-gray-300 transition-colors hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Annuler
              </button>
              <button
                onClick={handleChangeStatus}
                disabled={loadingChangeStatus}
                className="flex flex-1 gap-2 justify-center items-center px-4 py-3 font-bold text-white bg-green-600 rounded-lg transition-colors hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {loadingChangeStatus ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Traitement...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Confirmer
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Vérification Statut */}
      {showStatusModal && (
        <div className="flex overflow-y-auto fixed inset-0 z-50 justify-center items-center p-4 bg-black bg-opacity-50">
          <div className="my-8 w-full max-w-2xl bg-white rounded-2xl shadow-2xl">
            <div className="flex flex-shrink-0 justify-between items-center px-6 py-5 bg-gradient-to-r from-purple-600 to-purple-700 rounded-t-2xl">
              <h2 className="text-xl font-bold text-white">Vérification du Statut</h2>
              <button
                onClick={closeStatusModal}
                className="flex-shrink-0 p-1 text-white rounded-lg transition-colors hover:bg-purple-800"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="overflow-y-auto max-h-[calc(100vh-200px)]">
              <div className="p-6">
                {!statusSide ? (
                  <div className="space-y-3">
                    <p className="mb-4 text-sm font-bold text-gray-600">Sélectionnez le côté à vérifier:</p>
                    {loadingSides ? (
                      <div className="flex justify-center items-center py-8">
                        <div className="w-8 h-8 rounded-full border-purple-600 animate-spin border-2 border-t-transparent"></div>
                      </div>
                    ) : (
                      verificationSides.map((side, index) => (
                        <button
                          key={side.key}
                          onClick={() => handleCheckStatus(side.key)}
                          disabled={loadingStatus}
                          className={`px-4 py-4 w-full text-base font-bold text-white rounded-xl transition-colors disabled:bg-gray-400 ${
                            index === 0 ? "bg-blue-600 hover:bg-blue-700" : "bg-green-600 hover:bg-green-700"
                          }`}
                        >
                          {side.label}
                        </button>
                      ))
                    )}
                  </div>
                ) : loadingStatus ? (
                  <div className="flex justify-center items-center py-8">
                    <div className="w-10 h-10 rounded-full border-purple-600 animate-spin border-3 border-t-transparent"></div>
                  </div>
                ) : statusData?.error ? (
                  <div className="p-4 bg-red-50 rounded-xl border-2 border-red-200">
                    <p className="text-sm font-semibold text-red-700">{statusData.error}</p>
                  </div>
                ) : statusData ? (
                  <OperatorStatusResult data={statusData} sideLabel={statusSideLabel} />
                ) : null}
              </div>
            </div>

            <div className="flex flex-shrink-0 gap-3 px-6 py-4 bg-gray-50 rounded-b-2xl border-t-2 border-gray-200">
              {statusSide && (
                <button
                  onClick={() => {
                    setStatusData(null);
                    setStatusSide(null);
                  }}
                  className="flex-1 px-4 py-3 font-bold text-gray-700 rounded-lg border-2 border-gray-300 transition-colors hover:bg-gray-100"
                >
                  Retour
                </button>
              )}
              <button
                onClick={closeStatusModal}
                className="flex-1 px-4 py-3 font-bold text-gray-700 rounded-lg border-2 border-gray-300 transition-colors hover:bg-gray-100"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TransactionItem;
